import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
  Table,
  Badge,
} from 'reactstrap';
import Loading from '../../components/Loading';
import { cotizacionesService } from '../../services/cotizacionesService';
import { productosService } from '../../services/productosService';
import { clientesService } from '../../services/clientesService';
import { empresasService } from '../../services/empresasService';
import SnowFallComponent from 'components/SnowFallComponent';


function CotizacionForm({ sucursalId, usuarioId, onSuccess, onCancel }) {
  const IVA_PORCENTAJE = 16; // IVA fijo del 16%
  const [formData, setFormData] = useState({
    ClienteID: null,
    Folio: '', // Se generará automáticamente si está vacío
    Observaciones: '',
  });
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [buscarCliente, setBuscarCliente] = useState('');
  const [clientesEncontrados, setClientesEncontrados] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [detalles, setDetalles] = useState([]);
  const [productoBuscar, setProductoBuscar] = useState('');
  const [productosEncontrados, setProductosEncontrados] = useState([]);
  const [buscandoProducto, setBuscandoProducto] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [empresa, setEmpresa] = useState(null);

  useEffect(() => {
    loadEmpresa();
  }, []);

  const loadEmpresa = async () => {
    try {
      const response = await empresasService.getAll();
      if (response.success && response.data) {
        const empresasData = Array.isArray(response.data) ? response.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        setEmpresa(empresaActiva);
      }
    } catch (err) {
    }
  };

  const buscarClientes = async () => {
    if (!buscarCliente.trim() || !empresa) return;
    try {
      setBuscandoCliente(true);
      const response = await clientesService.buscar(buscarCliente.trim(), empresa.EmpresaID || empresa.empresaID);
      if (response.success && response.data) {
        const clientesData = Array.isArray(response.data) ? response.data : [];
        setClientesEncontrados(clientesData);
      }
    } catch (err) {
    } finally {
      setBuscandoCliente(false);
    }
  };

  const seleccionarCliente = (cliente) => {
    setClienteSeleccionado(cliente);
    setFormData(prev => ({ ...prev, ClienteID: cliente.ClienteID || cliente.clienteID }));
    setBuscarCliente('');
    setClientesEncontrados([]);
  };

  const buscarProductos = async () => {
    if (!productoBuscar.trim() || !empresa) return;
    try {
      setBuscandoProducto(true);
      const response = await productosService.getAll(
        empresa.EmpresaID || empresa.empresaID,
        sucursalId,
        null,
        productoBuscar.trim()
      );
      if (response.success && response.data) {
        const productosData = Array.isArray(response.data) ? response.data : [];
        setProductosEncontrados(productosData);
      }
    } catch (err) {
    } finally {
      setBuscandoProducto(false);
    }
  };

  const agregarProducto = async (producto) => {
    try {
      // En cotizaciones NO validamos stock, solo agregamos el producto
      const nuevoDetalle = {
        ProductoID: producto.ProductoID || producto.productoID,
        ProductoNombre: producto.Nombre || producto.nombre,
        ProductoSKU: producto.SKU || producto.sku,
        Cantidad: 1,
        PrecioUnitario: producto.PrecioVentaGlobal !== undefined ? parseFloat(producto.PrecioVentaGlobal) : (producto.precioVentaGlobal !== undefined ? parseFloat(producto.precioVentaGlobal) : 0),
        Descuento: 0,
        DescuentoTipo: 'monto', // 'monto' o 'porcentaje'
        ImpuestosPorcentaje: IVA_PORCENTAJE, // IVA fijo 16%
      };

      setDetalles(prev => [...prev, nuevoDetalle]);
      setProductoBuscar('');
      setProductosEncontrados([]);
      setError('');
    } catch (err) {
      setError(err.message || 'Error al agregar producto');
    }
  };

  const actualizarDetalle = (index, campo, valor) => {
    setDetalles(prev => {
      const nuevos = [...prev];
      if (campo === 'DescuentoTipo') {
        nuevos[index] = { ...nuevos[index], [campo]: valor };
      } else if (campo === 'ImpuestosPorcentaje') {
        // No permitir editar el IVA, siempre es 16%
        nuevos[index] = { ...nuevos[index], ImpuestosPorcentaje: IVA_PORCENTAJE };
      } else {
        nuevos[index] = { ...nuevos[index], [campo]: parseFloat(valor) || 0 };
      }
      return nuevos;
    });
  };

  const eliminarDetalle = (index) => {
    setDetalles(prev => prev.filter((_, i) => i !== index));
  };


  const calcularSubtotal = () => {
    // NUEVA LÓGICA: Descuento se aplica al precio unitario, luego se calcula subtotal
    return detalles.reduce((sum, det) => {
      // 1. Aplicar descuento al precio unitario
      let precioUnitarioConDescuento = det.PrecioUnitario;
      if (det.DescuentoTipo === 'porcentaje' && det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario * (1 - det.Descuento / 100);
      } else if (det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario - det.Descuento;
      }
      // 2. Calcular subtotal con precio unitario con descuento
      return sum + (precioUnitarioConDescuento * det.Cantidad);
    }, 0);
  };

  const calcularTotal = () => {
    const subtotal = calcularSubtotal();
    
    // Calcular suma de impuestos por detalle (IVA 16% fijo)
    const sumaImpuestosPorDetalle = detalles.reduce((sum, det) => {
      // 1. Aplicar descuento al precio unitario
      let precioUnitarioConDescuento = det.PrecioUnitario;
      if (det.DescuentoTipo === 'porcentaje' && det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario * (1 - det.Descuento / 100);
      } else if (det.Descuento > 0) {
        precioUnitarioConDescuento = det.PrecioUnitario - det.Descuento;
      }
      // 2. Calcular subtotal con precio unitario con descuento
      const subtotalDetalle = precioUnitarioConDescuento * det.Cantidad;
      // 3. Calcular IVA 16% sobre el subtotal
      const porcentajeIva = det.ImpuestosPorcentaje > 0 ? det.ImpuestosPorcentaje : IVA_PORCENTAJE;
      const impuestosDetalle = subtotalDetalle * (porcentajeIva / 100);
      return sum + impuestosDetalle;
    }, 0);
    
    // Total = subtotal + impuestos por detalle (sin descuentos ni impuestos globales)
    return subtotal + sumaImpuestosPorDetalle;
  };

  const validate = () => {
    if (detalles.length === 0) {
      setError('Debe agregar al menos un producto');
      return false;
    }

    // En cotizaciones NO validamos stock ni métodos de pago
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const cotizacionData = {
        SucursalID: sucursalId,
        ClienteID: formData.ClienteID,
        UsuarioID: usuarioId,
        Folio: formData.Folio || null, // Si está vacío, el backend generará uno automáticamente
        DescuentoTotal: 0, // Ya no hay descuentos globales
        DescuentoTotalTipo: 'monto',
        ImpuestosPorcentaje: 0, // Ya no hay impuestos globales
        Observaciones: formData.Observaciones || null,
        Detalles: detalles.map(det => ({
          ProductoID: det.ProductoID,
          Cantidad: det.Cantidad,
          PrecioUnitario: det.PrecioUnitario,
          Descuento: det.Descuento || 0,
          DescuentoTipo: det.DescuentoTipo || 'monto',
          ImpuestosPorcentaje: det.ImpuestosPorcentaje || IVA_PORCENTAJE, // IVA 16% por defecto
        })),
      };

      const response = await cotizacionesService.create(cotizacionData);

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || 'Error al crear la cotización');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">Nueva Cotización</CardTitle>
              <p className="text-muted">Las cotizaciones no afectan el inventario. Puedes convertirla a venta después.</p>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Row className="mb-3">
                  <Col md="6">
                    <label>Cliente (Opcional)</label>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Buscar cliente por nombre, RFC o correo"
                        value={buscarCliente}
                        onChange={(e) => setBuscarCliente(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            buscarClientes();
                          }
                        }}
                      />
                      <InputGroupText>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            buscarClientes();
                          }}
                          disabled={buscandoCliente}
                        >
                          <i className="tim-icons icon-zoom-split" />
                        </Button>
                      </InputGroupText>
                    </InputGroup>
                    {clientesEncontrados.length > 0 && (
                      <div className="list-group mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {clientesEncontrados.map((cliente) => (
                          <button
                            key={cliente.ClienteID || cliente.clienteID}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => seleccionarCliente(cliente)}
                          >
                            {cliente.NombreRazonSocial || cliente.nombreRazonSocial}
                            {cliente.RFC && ` - ${cliente.RFC || cliente.rfc}`}
                          </button>
                        ))}
                      </div>
                    )}
                    {clienteSeleccionado && (
                      <Badge color="success" className="mt-2">
                        Cliente: {clienteSeleccionado.NombreRazonSocial || clienteSeleccionado.nombreRazonSocial}
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 ml-2"
                          onClick={() => {
                            setClienteSeleccionado(null);
                            setFormData(prev => ({ ...prev, ClienteID: null }));
                          }}
                        >
                          <i className="tim-icons icon-simple-remove" />
                        </Button>
                      </Badge>
                    )}
                  </Col>
                  <Col md="3">
                    <label>Folio</label>
                    <Input
                      type="text"
                      value={formData.Folio}
                      onChange={(e) => setFormData(prev => ({ ...prev, Folio: e.target.value }))}
                      placeholder="Opcional"
                    />
                  </Col>
                  <Col md="3">
                    <label>Observaciones</label>
                    <Input
                      type="textarea"
                      value={formData.Observaciones}
                      onChange={(e) => setFormData(prev => ({ ...prev, Observaciones: e.target.value }))}
                      placeholder="Opcional"
                      rows="1"
                    />
                  </Col>
                </Row>

                <Row className="mb-3">
                  <Col md="12">
                    <label>Agregar Producto</label>
                    <InputGroup>
                      <Input
                        type="text"
                        placeholder="Buscar producto por SKU, nombre o código de barras"
                        value={productoBuscar}
                        onChange={(e) => setProductoBuscar(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            buscarProductos();
                          }
                        }}
                      />
                      <InputGroupText>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.preventDefault();
                            buscarProductos();
                          }}
                          disabled={buscandoProducto}
                        >
                          <i className="tim-icons icon-zoom-split" />
                        </Button>
                      </InputGroupText>
                    </InputGroup>
                    {productosEncontrados.length > 0 && (
                      <div className="list-group mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {productosEncontrados.map((producto) => (
                          <button
                            key={producto.ProductoID || producto.productoID}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onClick={() => agregarProducto(producto)}
                          >
                            {producto.SKU || producto.sku} - {producto.Nombre || producto.nombre}
                            {' '}
                            <Badge color="info">
                              {formatCurrency(producto.PrecioVentaGlobal !== undefined ? producto.PrecioVentaGlobal : (producto.precioVentaGlobal !== undefined ? producto.precioVentaGlobal : 0))}
                            </Badge>
                          </button>
                        ))}
                      </div>
                    )}
                  </Col>
                </Row>

                {detalles.length > 0 && (
                  <Row className="mb-3">
                    <Col md="12">
                      <Table responsive>
                        <thead>
                          <tr>
                            <th>Producto</th>
                            <th>SKU</th>
                            <th>Cantidad</th>
                            <th>Precio Unit.</th>
                            <th>Descuento</th>
                            <th>Impuestos %</th>
                            <th>Total</th>
                            {/* <th>Acciones</th> */}
                          </tr>
                        </thead>
                        <tbody>
                          {detalles.map((detalle, index) => {
                            // Calcular subtotal del detalle con nueva lógica (descuento al precio unitario)
                            // 1. Aplicar descuento al precio unitario
                            let precioUnitarioConDescuento = detalle.PrecioUnitario;
                            if (detalle.DescuentoTipo === 'porcentaje' && detalle.Descuento > 0) {
                              precioUnitarioConDescuento = detalle.PrecioUnitario * (1 - detalle.Descuento / 100);
                            } else if (detalle.Descuento > 0) {
                              precioUnitarioConDescuento = detalle.PrecioUnitario - detalle.Descuento;
                            }
                            // 2. Calcular subtotal con precio unitario con descuento
                            const subtotalDetalle = precioUnitarioConDescuento * detalle.Cantidad;
                            // 3. Calcular IVA 16% sobre el subtotal
                            const porcentajeIva = detalle.ImpuestosPorcentaje > 0 ? detalle.ImpuestosPorcentaje : IVA_PORCENTAJE;
                            const impuestosDetalle = subtotalDetalle * (porcentajeIva / 100);
                            const totalDetalle = subtotalDetalle + impuestosDetalle;
                            
                            return (
                              <tr key={index}>
                                <td>{detalle.ProductoNombre}</td>
                                <td>{detalle.ProductoSKU}</td>
                                <td>
                                  <Input
                                    type="number"
                                    min="0.01"
                                    step="0.01"
                                    value={detalle.Cantidad || ''}
                                    onChange={(e) => actualizarDetalle(index, 'Cantidad', e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    style={{ width: '80px' }}
                                  />
                                </td>
                                <td>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={detalle.PrecioUnitario || ''}
                                    onChange={(e) => actualizarDetalle(index, 'PrecioUnitario', e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    style={{ width: '100px' }}
                                  />
                                </td>
                                <td>
                                  <InputGroup>
                                    <Input
                                      type="select"
                                      value={detalle.DescuentoTipo || 'monto'}
                                      onChange={(e) => {
                                        setDetalles(prev => {
                                          const nuevos = [...prev];
                                          nuevos[index] = { ...nuevos[index], DescuentoTipo: e.target.value };
                                          return nuevos;
                                        });
                                      }}
                                      style={{ width: '90px' }}
                                    >
                                      <option value="monto">Monto</option>
                                      <option value="porcentaje">%</option>
                                    </Input>
                                    <Input
                                      type="number"
                                      min="0"
                                      step={detalle.DescuentoTipo === 'porcentaje' ? '0.1' : '0.01'}
                                      max={detalle.DescuentoTipo === 'porcentaje' ? '100' : undefined}
                                      value={detalle.Descuento || ''}
                                      onChange={(e) => actualizarDetalle(index, 'Descuento', e.target.value)}
                                      onFocus={(e) => e.target.select()}
                                      style={{ width: '100px' }}
                                      placeholder={detalle.DescuentoTipo === 'porcentaje' ? '%' : '$'}
                                    />
                                  </InputGroup>
                                </td>
                                <td>
                                  <div>
                                    <strong>{IVA_PORCENTAJE}%</strong>
                                    <small className="text-muted d-block">
                                      {formatCurrency(impuestosDetalle)}
                                    </small>
                                  </div>
                                </td>
                                <td>{formatCurrency(totalDetalle)}</td>
                                <td>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => eliminarDetalle(index)}
                                  >
                                    <i className="tim-icons icon-simple-remove" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Col>
                  </Row>
                )}

                <Row className="mb-3">
                  <Col md="6">
                    <label>IVA</label>
                    <div>
                      <strong>{IVA_PORCENTAJE}%</strong>
                      <small className="text-muted d-block">Impuesto fijo aplicado a cada producto</small>
                    </div>
                  </Col>
                  <Col md="6">
                    {(() => {
                      const subtotal = calcularSubtotal();
                      
                      // Calcular suma de impuestos por detalle (IVA 16% fijo)
                      const sumaImpuestosPorDetalle = detalles.reduce((sum, det) => {
                        // 1. Aplicar descuento al precio unitario
                        let precioUnitarioConDescuento = det.PrecioUnitario;
                        if (det.DescuentoTipo === 'porcentaje' && det.Descuento > 0) {
                          precioUnitarioConDescuento = det.PrecioUnitario * (1 - det.Descuento / 100);
                        } else if (det.Descuento > 0) {
                          precioUnitarioConDescuento = det.PrecioUnitario - det.Descuento;
                        }
                        // 2. Calcular subtotal con precio unitario con descuento
                        const subtotalDetalle = precioUnitarioConDescuento * det.Cantidad;
                        // 3. Calcular IVA 16% sobre el subtotal
                        const porcentajeIva = det.ImpuestosPorcentaje > 0 ? det.ImpuestosPorcentaje : IVA_PORCENTAJE;
                        const impuestosDetalle = subtotalDetalle * (porcentajeIva / 100);
                        return sum + impuestosDetalle;
                      }, 0);
                      
                      const total = calcularTotal();
                      
                      return (
                        <>
                          <h5 className="mt-4">
                            Subtotal: {formatCurrency(subtotal)}
                          </h5>
                          {sumaImpuestosPorDetalle > 0 && (
                            <h6 className="text-muted">
                              IVA ({IVA_PORCENTAJE}%): +{formatCurrency(sumaImpuestosPorDetalle)}
                            </h6>
                          )}
                          <h5>
                            Total: {formatCurrency(total)}
                          </h5>
                        </>
                      );
                    })()}
                  </Col>
                </Row>


                <Row>
                  <Col md="12" className="text-right">
                    <Button color="secondary" onClick={onCancel} className="mr-2">
                      Cancelar
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                      {loading ? 'Guardando...' : 'Guardar Cotización'}
                    </Button>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default CotizacionForm;

